/**
 * Shared between the playlist editor and player — the two halves of the
 * reel surface, ported from the Jinja pair (playlists/editor.html,
 * playlists/player.html) with the same API contract and event names.
 */
import { servedFps } from '../../lib/frameRate'
import {
  detectFramesIncludeGap, syncFieldsOf, type SyncState,
} from '../../lib/viz/frameTime'

export type PlaylistMeta = {
  id: string
  name: string
  visibility: 'private' | 'team'
  source: string
  rule: {
    moment_types?: string[]
    zones?: string[]
    min_score?: number
    player_jersey?: number | null
  } | null
  export?: { status?: string; url?: string } | null
  is_owner: boolean
}

export type PlaylistItem = {
  id: string
  game_code: string
  frame: number
  start_frame: number | null
  end_frame: number | null
  moment_type: string | null
  title: string | null
  note: string | null
  clip_url: string | null
  score: number | null
}

export type GameMeta = {
  video_url: string | null
  fps: number | null
  video_frame_aligned?: boolean
  video_offset_h1_s?: number | null
  video_offset_h2_s?: number | null
  video_time_map?: [number, number][] | null
  video_game_start_time?: number | null
  video_halftime_start_time?: number | null
  video_halftime_end_time?: number | null
  /** game_facts' data-halftime boundary — feeds detectFramesIncludeGap. */
  half2_frame?: number | null
}

export type PlaylistPayload = {
  playlist: PlaylistMeta
  items: PlaylistItem[]
  games: Record<string, GameMeta>
}

export function titleFor(item: PlaylistItem): string {
  if (item.title) return item.title
  const t = (item.moment_type || 'moment').replace(/_/g, ' ')
  return t.charAt(0).toUpperCase() + t.slice(1)
}

/** The moment's match clock at the game's own rate; '--:--' when the
 *  server sent none (the Jinja pair guessed 25 in one place and 30 in
 *  another for the same game). */
export function clockFor(item: PlaylistItem, g: GameMeta | undefined): string {
  const fps = servedFps(g?.fps)
  if (fps === null) return '--:--'
  const total = Math.floor(item.frame / fps)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** The canonical SyncState for a game's payload row — frame→time math
 *  lives ONLY in lib/viz/frameTime.ts (the Jinja player carried its own
 *  copy without the video-clock branch and seeked H2 clips ~15 minutes
 *  late on censused games; detectFramesIncludeGap ends that). Null when
 *  the server sent no frame rate: a frame cannot be placed in the video
 *  without one, so the caller seeks nothing. */
export function toSyncState(g: GameMeta): SyncState | null {
  const fps = servedFps(g.fps)
  if (fps === null) return null
  const sync: SyncState = {
    videoGameStartTime: g.video_game_start_time ?? 0,
    videoHalftimeStartTime: g.video_halftime_start_time ?? null,
    videoHalftimeEndTime: g.video_halftime_end_time ?? null,
    ...syncFieldsOf(g),
    fps,
  }
  sync.framesIncludeHalftimeGap =
    detectFramesIncludeGap(sync, g.half2_frame ?? null)
  return sync
}
